import { t } from "../../i18n";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { fmt, makeDefaultDrawing, type Pt, type WallInput, type WallResult } from "@tasduvar/engine";
import { LockedNote, requireFullAccess, useFullAccess } from "../../billing/access";
import { useProjectStore, useSelectedWall } from "../../state/projectStore";
import { useAnalysis } from "../../state/useAnalysis";
import { useCadUi } from "../../state/cadUiStore";
import { downloadBytes } from "../../lib/download";
import { buildDxf, buildSheet, encodeDxf, levelPrims, type SheetLayout, type SheetPrim } from "../../export/dxf";
import { about, addedIndex, addedKey, boxSelect, compose, hitTest, IDENTITY, mirrorAbout, newAddedId, restyle, snapPoint, transformPrim, translate, type Tf } from "../../export/dxf/edits";
import { useSheetEdit, type SheetTool } from "../../state/sheetEditStore";
import { parseTR } from "../../lib/number";
import { fileDigest } from "../report/hash";
import { CadFormContext } from "../../ui/formContext";
import { DrawingTab } from "../wall-editor/DrawingTab";
import { Palette, PaletteButton } from "../cad/Palette";
import { CommandLine } from "../cad/CommandLine";
import { LayoutTabs } from "../cad/LayoutTabs";
import { SheetSvg } from "./SheetSvg";

type Sheet = NonNullable<WallInput["drawing"]>["sheet"];
type Op =
  | { kind: "window"; a: Pt; b: Pt }
  | { kind: "pan"; x: number; y: number; vb: View }
  | { kind: "drag"; start: Pt; cur: Pt }
  | { kind: "base" }
  | { kind: "second"; base: Pt; cur: Pt }
  | { kind: "value"; base: Pt; cur: Pt; input: string }
  | { kind: "line"; pts: Pt[]; cur: Pt | null }
  | { kind: "dim"; pts: Pt[]; cur: Pt | null }
  | { kind: "textinput"; p: Pt; value: string; key?: string; h?: number; /** çok satırlı yazı (MTEXT): Enter yeni satır, Ctrl+Enter bitirir */ multi?: boolean }
  | { kind: "tableinput"; p: Pt; value: string }
  | { kind: "level" };
interface View { x: number; y: number; w: number; h: number }

/** Paftanın DXF/baskı seçenekleri (proje adı + hesap özeti antede) */
function sheetOptions(projectName: string, digest: string): { projectName: string; digest: string } { return { projectName, digest }; }

const PROMPT: Record<SheetTool, string> = {
  select: "Nesne seçin (tıklama · pencere: soldan sağa içindekiler, sağdan sola değenler) · Shift ekler",
  move: "TAŞI", copy: "KOPYALA", scale: "ÖLÇEKLE", rotate: "DÖNDÜR", mirror: "AYNA", line: "ÇİZGİ", text: "YAZI", dim: "ÖLÇÜ", table: "TABLO", level: "KOT",
};

/**
 * Çizim paftası (Cem 2026-09-29, AutoCAD layout gibi; "AutoCAD'e ihtiyacımız kalmasın"): solda pafta ve çizim ayarları; ortada beyaz
 * kâğıt (DXF ile aynı öğeler). Seç (Blok / Nesne), Taşı · Kopyala · Ölçekle · Döndür · Sil · Yazı düzelt · Çizgi · Yazı ekle; uç nokta
 * yakalama, pencere/kesişen seçim, Geri al/Yinele. Elle düzenlemeler `drawing.sheet.edits`'e yazılır ve DXF'e / baskıya aynen gider.
 * Fare: tekerlek yakınlaştırır, orta tuş (ya da boşlukla sürükleme) kaydırır.
 */
export function DrawingLayout(): JSX.Element {
  const wall = useSelectedWall();
  const analysis = useAnalysis(wall);
  const { project, updateWall, readOnly } = useProjectStore();
  const cad = useCadUi();
  const full = useFullAccess();
  const ed = useSheetEdit();
  const [op, setOpState] = useState<Op | null>(null);
  // İşaretçi olayları arka arkaya (yeniden çizimden önce) gelebilir: işleyiciler güncel adımı bu ref'ten okur
  const opRef = useRef<Op | null>(null);
  const setOp = useCallback((o: Op | null): void => { opRef.current = o; setOpState(o); }, []);
  const [hover, setHover] = useState<{ p: Pt; snap: Pt | null } | null>(null);
  const [vb, setVb] = useState<View | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const space = useRef(false);
  const r = analysis.result;
  const layout = useMemo<SheetLayout | null>(() => {
    if (!wall || !r || !project) return null;
    try { return buildSheet(wall, r, sheetOptions(project.info.name, fileDigest(project))); } catch { return null; }
  }, [wall, r, project]);
  const frameKey = layout ? `${layout.frame.minX.toFixed(2)}:${layout.frame.maxY.toFixed(2)}:${layout.paperMm.w}:${layout.paperMm.h}` : "";
  useEffect(() => { setVb(null); }, [frameKey, cad.zoomTo]);
  const byKey = useMemo(() => new Map((layout?.prims ?? []).filter((e) => e.key).map((e) => [e.key!, e])), [layout]);
  const selected = useMemo(() => ed.selection.map((k) => byKey.get(k)).filter((e): e is SheetPrim => !!e), [ed.selection, byKey]);

  const fitVb = layout ? { x: layout.frame.minX - layout.th * 4, y: -layout.frame.maxY - layout.th * 4, w: layout.frame.maxX - layout.frame.minX + layout.th * 8, h: layout.frame.maxY - layout.frame.minY + layout.th * 8 } : null;
  const view = vb ?? fitVb;
  const pxToM = (px: number): number => (view && svgRef.current ? (px * view.w) / Math.max(1, svgRef.current.clientWidth) : 0.1);
  const toModel = useCallback((clientX: number, clientY: number): Pt => {
    const m = svgRef.current?.getScreenCTM();
    if (!m) return { x: 0, y: 0 };
    const q = new DOMPoint(clientX, clientY).matrixTransform(m.inverse());
    return { x: q.x, y: -q.y };
  }, []);
  const toScreen = (p: Pt): { left: number; top: number } => {
    const svg = svgRef.current, m = svg?.getScreenCTM(), box = svg?.getBoundingClientRect();
    if (!m || !box) return { left: 0, top: 0 };
    const q = new DOMPoint(p.x, -p.y).matrixTransform(m);
    return { left: q.x - box.left, top: q.y - box.top };
  };

  // ── Kayıt: her değişiklik proje geçmişinde ayrı bir Geri al adımı ───────────
  const commit = (recipe: (s: Sheet) => void): void => {
    if (!wall || readOnly) return;
    useProjectStore.getState().checkpoint();
    updateWall(wall.id, (w) => { w.drawing ??= makeDefaultDrawing(); w.drawing.sheet ??= makeDefaultDrawing().sheet; w.drawing.sheet.edits ??= { overrides: {}, added: [], exploded: [], blocks: {} }; recipe(w.drawing.sheet); });
    useProjectStore.getState().checkpoint();
  };
  const restore = (dir: "undo" | "redo"): void => { useProjectStore.getState()[dir](); ed.setSelection([]); };

  /** Eklenen öğeleri kimlik vererek ekler; `asBlock` → hepsi tek kullanıcı bloğu (tablo, kot işareti, çoklu kopya) */
  const addPrims = (s: Sheet, prims: Record<string, unknown>[], asBlock = false): void => {
    const keys: string[] = [];
    for (const p of prims) { const id = newAddedId(); s.edits.added.push({ ...p, id }); keys.push(`u:${id}`); }
    if (asBlock && keys.length > 1) { s.edits.blocks ??= {}; s.edits.blocks[`B${Date.now().toString(36)}`] = keys; }
  };
  const replaceAdded = (s: Sheet, key: string, prim: Record<string, unknown>): void => { const i = addedIndex(s.edits.added, key); if (i >= 0) s.edits.added[i] = { ...prim, id: s.edits.added[i]!.id }; };

  /** Seçime dönüşüm uygula: tam blok + öteleme → blok kaydırması; değilse öğe düzenlemesi; kopyada yeni öğe */
  const applyToSelection = (tf: Tf, copy: boolean): void => {
    if (!selected.length || !layout) return;
    const groups = new Set(selected.map((e) => e.g));
    const whole = [...groups].every((g) => g !== "user" && g !== "cerceve" && layout.prims.filter((e) => e.g === g).every((e) => ed.selection.includes(e.key!)));
    commit((s) => {
      if (copy) { addPrims(s, selected.map((e) => strip(transformPrim(e, tf))), selected.length > 1); return; }
      if (whole && tf.s === 1 && tf.r === 0) {
        for (const g of groups) { const b = layout.blocks.find((x) => x.id === g); const o = b?.offset ?? { x: 0, y: 0 }; s.offsets = { ...s.offsets, [g]: { dx: o.x + tf.tx, dy: o.y + tf.ty } }; }
        return;
      }
      for (const e of selected) {
        if (e.key!.startsWith("u:")) { replaceAdded(s, e.key!, strip(transformPrim(e, tf))); continue; }
        const ov = s.edits.overrides[e.key!] ?? { fp: e.fp ?? "" };
        s.edits.overrides[e.key!] = { ...ov, fp: e.fp ?? ov.fp, tf: compose(ov.tf ?? IDENTITY, tf) };
      }
    });
  };
  const deleteSelection = (): void => {
    if (!selected.length) return;
    commit((s) => {
      const drop = new Set<string>();
      for (const e of selected) {
        if (e.key!.startsWith("u:")) drop.add(e.key!);
        else s.edits.overrides[e.key!] = { ...(s.edits.overrides[e.key!] ?? {}), fp: e.fp ?? "", del: true };
      }
      s.edits.added = s.edits.added.filter((a, i) => !drop.has(addedKey(a, i)));
      for (const [name, ks] of Object.entries(s.edits.blocks ?? {})) { const rest = ks.filter((k) => !ed.selection.includes(k)); if (rest.length > 1) s.edits.blocks[name] = rest; else delete s.edits.blocks[name]; }
    });
    ed.setSelection([]);
  };
  const startTextEdit = (e: SheetPrim | undefined): void => {
    if (!e || (e.k !== "text" && e.k !== "mtext" && e.k !== "dim")) return;
    const value = e.k === "text" ? e.value : e.k === "mtext" ? e.lines.join("\n") : e.text ?? "";
    setOp({ kind: "textinput", p: e.k === "dim" ? e.a : e.p, value, key: e.key, h: e.k === "text" ? e.opts.h : e.k === "mtext" ? e.h : undefined, multi: e.k === "mtext" });
  };
  const finishText = (o: Extract<Op, { kind: "textinput" }>): void => {
    setOp(null);
    if (!o.key) { if (o.value.trim()) commit((s) => { addPrims(s, [{ k: "text", layer: "KULLANICI", p: o.p, value: o.value, opts: { h: layout?.th ?? 0.125 } }]); }); ed.setTool("select"); return; }
    const e = byKey.get(o.key); if (!e) return;
    commit((s) => {
      if (o.key!.startsWith("u:")) { const a = s.edits.added[addedIndex(s.edits.added, o.key!)]; if (!a) return; if (e.k === "text") { a.value = o.value; if (o.h) a.opts = { ...(a.opts as object), h: o.h }; } else if (e.k === "mtext") a.lines = o.value.split("\n"); else a.text = o.value; return; }
      s.edits.overrides[o.key!] = { ...(s.edits.overrides[o.key!] ?? {}), fp: e.fp ?? "", text: o.value, ...(o.h ? { h: o.h } : {}) };
    });
  };
  const finishLine = (pts: Pt[]): void => {
    setOp(null);
    if (pts.length >= 2) commit((s) => { addPrims(s, pts.slice(1).map((b, i) => ({ k: "line", layer: "KULLANICI", a: pts[i], b }))); });
  };

  /** Katman / renk: üretilen öğeye düzenleme, eklenen öğeye doğrudan */
  const restyleSelection = (st: { layer?: string; color?: number }): void => {
    if (!selected.length) return;
    commit((s) => {
      for (const e of selected) {
        if (e.key!.startsWith("u:")) { replaceAdded(s, e.key!, strip(restyle(e, st.layer, st.color))); continue; }
        const ov = s.edits.overrides[e.key!] ?? { fp: e.fp ?? "" };
        s.edits.overrides[e.key!] = { ...ov, fp: e.fp ?? ov.fp, ...(st.layer ? { layer: st.layer } : {}), ...(st.color !== undefined ? { color: st.color } : {}) };
      }
    });
  };
  /** Tablo ekle: satırlar alt satırla, hücreler sekme (Excel'den yapıştırma) ya da ; ile ayrılır → çizgi ızgarası + yazılar (KULLANICI) */
  const finishTable = (o: Extract<Op, { kind: "tableinput" }>): void => {
    setOp(null); ed.setTool("select");
    const prims = tablePrims(o.p, o.value, layout?.th ?? 0.125);
    if (prims.length) commit((s) => { addPrims(s, prims, true); });
  };
  useEffect(() => { const st = ed.style; if (!st) return; ed.setStyle(null); restyleSelection(st); }, [ed.style]);

  // Şeritten gelen istekler (Sil, Geri al, Yinele, Yazı düzelt, Düzenlemeleri sıfırla)
  useEffect(() => {
    const a = ed.action; if (!a) return;
    ed.request(null);
    if (a === "delete") deleteSelection();
    else if (a === "undo") restore("undo");
    else if (a === "redo") restore("redo");
    else if (a === "edittext") startTextEdit(selected[0]);
    else if (a === "reset") { commit((s) => { s.edits = { overrides: {}, added: [], exploded: [], blocks: {} }; s.offsets = {}; }); ed.setSelection([]); }
    else if (a === "explode") {
      // Patlat: seçimdeki program blokları tek tek seçilebilir olur, kullanıcı blokları çözülür
      if (!selected.length) return;
      commit((s) => {
        s.edits.exploded ??= []; s.edits.blocks ??= {};
        for (const g of new Set(selected.map((e) => e.g))) if (g !== "user" && g !== "cerceve" && !s.edits.exploded.includes(g)) s.edits.exploded.push(g);
        for (const [name, ks] of Object.entries(s.edits.blocks)) if (ks.some((k) => ed.selection.includes(k))) delete s.edits.blocks[name];
      });
      ed.setSelection([]);
    } else if (a === "block") {
      // Blok yap: seçili nesneler tek birim olarak seçilir/taşınır (patlatılmış program blokları yeniden toplanır)
      if (selected.length < 2) return;
      commit((s) => {
        s.edits.exploded ??= []; s.edits.blocks ??= {};
        const groups = new Set(selected.map((e) => e.g));
        const whole = [...groups].filter((g) => g !== "user" && g !== "cerceve" && layout!.prims.filter((x) => x.g === g).every((x) => ed.selection.includes(x.key!)));
        if (whole.length === groups.size) { s.edits.exploded = s.edits.exploded.filter((g) => !whole.includes(g)); return; }
        for (const [name, ks] of Object.entries(s.edits.blocks)) if (ks.some((k) => ed.selection.includes(k))) delete s.edits.blocks[name];
        s.edits.blocks[`B${Date.now().toString(36)}`] = [...ed.selection];
      });
    }
  }, [ed.action]);
  // Araç değişince: dönüşüm araçlarında seçim varsa temel nokta sorulur
  useEffect(() => {
    if (ed.tool === "move" || ed.tool === "copy" || ed.tool === "scale" || ed.tool === "rotate" || ed.tool === "mirror") setOp(ed.selection.length ? { kind: "base" } : null);
    else if (ed.tool === "line") setOp({ kind: "line", pts: [], cur: null });
    else if (ed.tool === "dim") setOp({ kind: "dim", pts: [], cur: null });
    else if (ed.tool === "level") setOp({ kind: "level" });
    else setOp(null);
  }, [ed.tool]);
  useEffect(() => () => { useSheetEdit.getState().setTool("select"); useSheetEdit.getState().setSelection([]); }, []);

  // Klavye (AutoCAD kısayolları): Esc · Delete · Ctrl+Z/Y · M taşı · C kopyala · S ölçekle · R döndür · L çizgi · T yazı · Enter bitir
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      const el = e.target instanceof HTMLElement ? e.target : null;
      if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT")) return;
      const st = useSheetEdit.getState();
      let handled = true;
      if (e.key === " ") { space.current = e.type === "keydown"; if (e.type === "keyup") return; }
      else if (e.type !== "keydown") return;
      else if (e.key === "Escape") { if (op?.kind === "line" && op.pts.length >= 2) finishLine(op.pts); else { setOp(null); if (st.tool !== "select") st.setTool("select"); else st.setSelection([]); } }
      else if (e.key === "Enter") { if (op?.kind === "line") finishLine(op.pts); else if (op?.kind === "value") commitValue(op); else if (!op && st.selection.length && st.tool !== "select") setOp({ kind: "base" }); else handled = false; }
      else if (e.key === "Delete" || e.key === "Backspace") deleteSelection();
      else if ((e.ctrlKey || e.metaKey) && (e.key === "z" || e.key === "Z")) restore(e.shiftKey ? "redo" : "undo");
      else if ((e.ctrlKey || e.metaKey) && (e.key === "y" || e.key === "Y")) restore("redo");
      else if (e.ctrlKey || e.metaKey || e.altKey) handled = false;
      else if (/^[mM]$/.test(e.key)) st.setTool("move");
      else if (/^[cC]$/.test(e.key)) st.setTool("copy");
      else if (/^[sS]$/.test(e.key)) st.setTool("scale");
      else if (/^[rR]$/.test(e.key)) st.setTool("rotate");
      else if (/^[lL]$/.test(e.key)) st.setTool("line");
      else if (/^[tT]$/.test(e.key)) st.setTool("text");
      else if (/^[dD]$/.test(e.key)) st.setTool("dim");
      else if (/^[aA]$/.test(e.key)) st.setTool("mirror");
      else if (/^[kK]$/.test(e.key)) st.setTool("level");
      else if (/^[xX]$/.test(e.key)) st.request("explode");
      else if (/^[bB]$/.test(e.key)) st.request("block");
      else if (/^[eE]$/.test(e.key)) startTextEdit(selected[0]);
      else handled = false;
      if (handled) { e.preventDefault(); e.stopImmediatePropagation(); }
    };
    window.addEventListener("keydown", onKey, true);
    window.addEventListener("keyup", onKey, true);
    return () => { window.removeEventListener("keydown", onKey, true); window.removeEventListener("keyup", onKey, true); };
  });

  if (!wall || !project) return <p className="p-6 text-cad-ink3">{t("Duvar seçin.")}</p>;
  const sheet = wall.drawing?.sheet ?? makeDefaultDrawing().sheet;

  const snapAt = (p: Pt): Pt => (layout ? snapPoint(layout.prims, p, pxToM(9)) ?? p : p);
  const ortho = (base: Pt, p: Pt, on: boolean): Pt => (on ? (Math.abs(p.x - base.x) >= Math.abs(p.y - base.y) ? { x: p.x, y: base.y } : { x: base.x, y: p.y }) : p);
  const angleOf = (base: Pt, p: Pt): number => (Math.atan2(p.y - base.y, p.x - base.x) * 180) / Math.PI;
  function commitValue(o: Extract<Op, { kind: "value" }>): void {
    const v = parseTR(o.input);
    if (v === null || v === 0) return;
    const st = useSheetEdit.getState();
    applyToSelection(st.tool === "scale" ? about(o.base, v, 0) : about(o.base, 1, v), false);
    setOp(null); st.setTool("select");
  }
  const pick = (p: Pt, add: boolean): void => {
    if (!layout) return;
    const e = hitTest(layout.prims, p, pxToM(6));
    if (!e?.key) { if (!add) ed.setSelection([]); return; }
    // Önce kullanıcı bloğu (Blok yap / tablo / kot), sonra patlatılmamış program bloğu (kesit, antet…), değilse tek nesne
    const edits = sheet.edits;
    const ub = Object.values(edits?.blocks ?? {}).find((ks) => ks.includes(e.key!));
    const keys = ub ? ub.filter((k) => byKey.has(k)) : e.g !== "user" && e.g !== "cerceve" && !(edits?.exploded ?? []).includes(e.g) ? layout.prims.filter((x) => x.g === e.g).map((x) => x.key!) : [e.key];
    ed.setSelection(add ? [...new Set([...ed.selection, ...keys])] : keys);
  };
  const afterSelect = (): void => { const st = useSheetEdit.getState(); if (st.selection.length && st.tool !== "select" && st.tool !== "line" && st.tool !== "text") setOp({ kind: "base" }); };

  const onDown = (e: React.PointerEvent<SVGSVGElement>): void => {
    const op = opRef.current;
    if (!view) return;
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
    if (e.button === 1 || space.current) { setOp({ kind: "pan", x: e.clientX, y: e.clientY, vb: view }); return; }
    if (e.button !== 0 || readOnly) return;
    const raw = toModel(e.clientX, e.clientY), p = snapAt(raw);
    if (op?.kind === "base") { setOp(ed.tool === "scale" ? { kind: "value", base: p, cur: p, input: "" } : { kind: "second", base: p, cur: p }); return; }
    if (op?.kind === "second") {
      const q = ortho(op.base, p, e.shiftKey);
      if (ed.tool === "rotate") applyToSelection(about(op.base, 1, angleOf(op.base, q)), false);
      else if (ed.tool === "mirror") { if (q.x !== op.base.x || q.y !== op.base.y) applyToSelection(mirrorAbout(op.base, q), !e.shiftKey); }   // AutoCAD gibi: ayna kopya üretir, Shift ile kaynak da silinir (taşınır)
      else applyToSelection(translate(q.x - op.base.x, q.y - op.base.y), ed.tool === "copy");
      if (ed.tool === "mirror") ed.setSelection([]);
      setOp(null); if (ed.tool !== "copy") ed.setTool("select"); else setOp({ kind: "base" });
      return;
    }
    if (op?.kind === "line") { const last = op.pts[op.pts.length - 1]; setOp({ ...op, pts: [...op.pts, last ? ortho(last, p, e.shiftKey) : p] }); return; }
    if (op?.kind === "dim") {
      if (op.pts.length < 2) { setOp({ ...op, pts: [...op.pts, p], cur: p }); return; }
      const d = dimFrom(op.pts[0]!, op.pts[1]!, raw);
      if (d.a.x !== d.b.x || d.a.y !== d.b.y) commit((s) => { addPrims(s, [d as unknown as Record<string, unknown>]); });
      setOp({ kind: "dim", pts: [], cur: null });   // ölçü aracı açık kalır (art arda ölçü), Esc ile çıkılır
      return;
    }
    if (ed.tool === "text") { setOp({ kind: "textinput", p, value: "" }); return; }
    if (ed.tool === "table") { setOp({ kind: "tableinput", p, value: "" }); return; }
    if (op?.kind === "level" && layout) { const v = levelAt(layout, p); commit((s) => { addPrims(s, levelPrims(p, v, layout.th, e.shiftKey ? "left" : "right"), true); }); return; }   // art arda kot; Esc ile çıkılır
    if (op?.kind === "value" || op?.kind === "textinput" || op?.kind === "tableinput") return;
    // Seç: seçili öğenin üstünde sürükleme → taşı; değilse pencere seçimi (bırakınca tıklama sayılırsa tek seçim)
    const hit = layout ? hitTest(layout.prims, raw, pxToM(6)) : null;
    if (hit?.key && ed.selection.includes(hit.key) && ed.tool === "select") { setOp({ kind: "drag", start: p, cur: p }); return; }
    setOp({ kind: "window", a: raw, b: raw });
  };
  const onMove = (e: React.PointerEvent<SVGSVGElement>): void => {
    const op = opRef.current;
    const raw = toModel(e.clientX, e.clientY);
    const sn = layout && op && op.kind !== "window" && op.kind !== "pan" ? snapPoint(layout.prims, raw, pxToM(9)) : null;
    setHover({ p: raw, snap: sn });
    const p = sn ?? raw;
    if (!op) return;
    if (op.kind === "pan") { const k = op.vb.w / Math.max(1, svgRef.current!.clientWidth); setVb({ ...op.vb, x: op.vb.x - (e.clientX - op.x) * k, y: op.vb.y - (e.clientY - op.y) * k }); }
    else if (op.kind === "window") setOp({ ...op, b: raw });
    else if (op.kind === "drag") setOp({ ...op, cur: ortho(op.start, p, e.shiftKey) });
    else if (op.kind === "second") setOp({ ...op, cur: ortho(op.base, p, e.shiftKey) });
    else if (op.kind === "value") setOp({ ...op, cur: p });
    else if (op.kind === "line") { const last = op.pts[op.pts.length - 1]; setOp({ ...op, cur: last ? ortho(last, p, e.shiftKey) : p }); }
    else if (op.kind === "dim") setOp({ ...op, cur: op.pts.length === 2 ? raw : p });
  };
  const onUp = (e: React.PointerEvent<SVGSVGElement>): void => {
    const op = opRef.current;
    if (!op) return;
    if (op.kind === "pan") setOp(ed.tool !== "select" && ed.selection.length ? { kind: "base" } : null);
    else if (op.kind === "drag") { if (op.cur.x !== op.start.x || op.cur.y !== op.start.y) applyToSelection(translate(op.cur.x - op.start.x, op.cur.y - op.start.y), false); setOp(null); }
    else if (op.kind === "window") {
      const small = Math.hypot(op.b.x - op.a.x, op.b.y - op.a.y) < pxToM(4);
      if (small) pick(op.a, e.shiftKey);
      else if (layout) { const keys = boxSelect(layout.prims, op.a, op.b).map((x) => x.key!).filter(Boolean); ed.setSelection(e.shiftKey ? [...new Set([...ed.selection, ...keys])] : keys); }
      setOp(null); afterSelect();
    }
  };
  const onWheel = (e: React.WheelEvent<SVGSVGElement>): void => {
    if (!view) return;
    const rect = svgRef.current!.getBoundingClientRect();
    const k = e.deltaY > 0 ? 1.15 : 1 / 1.15;
    const fx = (e.clientX - rect.left) / rect.width, fy = (e.clientY - rect.top) / rect.height;
    setVb({ x: view.x + (view.w - view.w * k) * fx, y: view.y + (view.h - view.h * k) * fy, w: view.w * k, h: view.h * k });
  };

  // Önizleme dönüşümü (taşı/kopyala/döndür/ölçekle ve sürükleme)
  const previewTf: Tf | null = !op ? null
    : op.kind === "drag" ? translate(op.cur.x - op.start.x, op.cur.y - op.start.y)
    : op.kind === "second" ? (ed.tool === "rotate" ? about(op.base, 1, angleOf(op.base, op.cur)) : ed.tool === "mirror" ? (op.cur.x === op.base.x && op.cur.y === op.base.y ? null : mirrorAbout(op.base, op.cur)) : translate(op.cur.x - op.base.x, op.cur.y - op.base.y))
    : op.kind === "value" && parseTR(op.input) ? (ed.tool === "scale" ? about(op.base, parseTR(op.input)!, 0) : about(op.base, 1, parseTR(op.input)!))
    : null;
  const prompt = ((): string => {
    const tool = ed.tool;
    if (tool === "select") return t(PROMPT.select);
    if (tool === "line") return `${t("ÇİZGİ")}: ${op?.kind === "line" && op.pts.length ? t("Sonraki nokta (Shift: dik) · Enter / Esc bitir") : t("İlk nokta")}`;
    if (tool === "text") return `${t("YAZI")}: ${t("Yazı noktası")}`;
    if (tool === "table") return `${t("TABLO")}: ${t("Tablonun sol üst köşesi")}`;
    if (tool === "level") return `${t("KOT")}: ${t("Kot noktasına tıklayın")}${hover && layout ? ` → ${fmtLevel(levelAt(layout, hover.snap ?? hover.p))}` : ""} (Shift: ${t("yazı solda")}) · Esc ${t("bitir")}`;
    if (tool === "mirror" && op?.kind === "second") return `${t("AYNA")}: ${t("Ayna doğrusunun ikinci noktası")} (Shift: ${t("kaynağı sil")})`;
    if (tool === "dim") { const n = op?.kind === "dim" ? op.pts.length : 0; return `${t("ÖLÇÜ")}: ${n === 0 ? t("Birinci nokta") : n === 1 ? t("İkinci nokta") : t("Ölçü çizgisinin yeri (yukarı/aşağı: yatay · sağa/sola: düşey)")} · Esc ${t("bitir")}`; }
    const name = t(PROMPT[tool]);
    if (!ed.selection.length) return `${name}: ${t("Nesne seçin")}`;
    if ((!op || op.kind === "base") && tool === "mirror") return `${name}: ${t("Ayna doğrusunun birinci noktası")} (${ed.selection.length} ${t("nesne")})`;
    if (!op || op.kind === "base") return `${name}: ${t("Temel nokta")} (${ed.selection.length} ${t("nesne")})`;
    if (op.kind === "second") return `${name}: ${tool === "rotate" ? t("Açı noktası") : t("İkinci nokta")} (Shift: ${t("dik")})`;
    if (op.kind === "value") return `${name}: ${tool === "scale" ? t("Ölçek çarpanı") : t("Açı (°)")} — Enter`;
    return name;
  })();
  const tint = "#2F6FDB";
  const sub = (prims: SheetPrim[]): SheetLayout | null => (layout ? { ...layout, prims, blocks: [] } : null);
  const selLayout = selected.length ? sub(selected) : null;
  const prevLayout = previewTf && selected.length ? sub(selected.map((e) => transformPrim(e, previewTf))) : null;
  const lineLayout = op?.kind === "line" && op.pts.length ? sub([...op.pts.slice(1).map((b, i) => ({ k: "line" as const, g: "user", layer: "KULLANICI", a: op.pts[i]!, b })), ...(op.cur ? [{ k: "line" as const, g: "user", layer: "KULLANICI", a: op.pts[op.pts.length - 1]!, b: op.cur }] : [])]) : null;
  const dimLayout = op?.kind === "dim" && op.pts.length && op.cur ? sub(op.pts.length === 2 ? [{ ...dimFrom(op.pts[0]!, op.pts[1]!, op.cur), g: "user" }] : [{ k: "line" as const, g: "user", layer: "OLCU", a: op.pts[0]!, b: op.cur }]) : null;
  const levelLayout = op?.kind === "level" && hover && layout ? sub(levelPrims(hover.snap ?? hover.p, levelAt(layout, hover.snap ?? hover.p), layout.th).map((x) => ({ ...(x as unknown as SheetPrim), g: "user" }))) : null;
  const editCount = Object.keys(sheet.edits?.overrides ?? {}).length + (sheet.edits?.added.length ?? 0) + Object.keys(sheet.offsets).length;

  return (
    <>
      {readOnly ? null : (
        <Palette title={`${t("Çizim paftası")} — ${wall.name}`} id="cad-sheet-settings" className="shrink-0 border-r border-cad-line" style={{ width: cad.palettes.left || 268 }}
          actions={<PaletteButton label={t("Özellikleri daralt")} onClick={() => cad.setPalettes({ left: cad.palettes.left > 100 ? 0 : 268 })}>⌄</PaletteButton>}
          footer={t("M taşı · C kopyala · S ölçekle · R döndür · A ayna · Del sil · E yazı düzelt · X patlat · B blok yap · L çizgi · T yazı · D ölçü · K kot · Ctrl+Z geri al · orta tuş / boşluk + sürükle kaydırır")}>
          <CadFormContext.Provider value={true}>
            <div className="cad-props h-full"><div className="cad-props-body grid overflow-auto" role="group" aria-label={t("Çizim ayarları")}>
              <DrawingTab wall={wall} update={(recipe) => updateWall(wall.id, recipe)} />
            </div></div>
          </CadFormContext.Provider>
        </Palette>
      )}
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        <div className="flex-1 relative min-h-0 bg-[#3A3F45] overflow-hidden" aria-label={t("Pafta alanı")}>
          {!full ? <div className="absolute inset-x-3 top-3 z-10"><LockedNote what={t("Çizim paftası çıktısı (DXF, yazdırma)")} /></div> : null}
          {analysis.error ? <p role="alert" className="absolute left-3 top-3 z-10 border border-[#B42318]/50 bg-cad-badbg px-2 py-1.5 text-[11px] text-[#B42318]">{t("Hesap yapılamadı:")} {analysis.error}</p> : null}
          {layout && view && full ? (
            <svg ref={svgRef} className={`absolute inset-0 w-full h-full touch-none select-none ${ed.tool === "select" && !op ? "cursor-default" : "cursor-crosshair"}`} viewBox={`${view.x} ${view.y} ${view.w} ${view.h}`} preserveAspectRatio="xMidYMid meet"
              onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={() => setOp(null)} onWheel={onWheel} onPointerLeave={() => setHover(null)}
              onDoubleClick={(e) => { if (!layout || readOnly) return; startTextEdit(hitTest(layout.prims, toModel(e.clientX, e.clientY), pxToM(6)) ?? undefined); }}
              onContextMenu={(e) => { e.preventDefault(); if (op?.kind === "line") finishLine(op.pts); else if (ed.selection.length && ed.tool !== "select") setOp({ kind: "base" }); }}
              aria-label={t("Çizim paftası önizlemesi")}>
              <rect x={layout.frame.minX} y={-layout.frame.maxY} width={layout.frame.maxX - layout.frame.minX} height={layout.frame.maxY - layout.frame.minY} fill="#000" opacity={0.35} transform={`translate(${layout.th * 0.8} ${layout.th * 0.8})`} />
              <MemoSheet layout={layout} />
              {selLayout ? <g opacity={prevLayout ? 0.35 : 0.9} data-selection>{<SheetSvg nested layout={selLayout} tint={tint} />}</g> : null}
              {prevLayout ? <SheetSvg nested layout={prevLayout} tint={tint} /> : null}
              {lineLayout ? <SheetSvg nested layout={lineLayout} tint={tint} /> : null}
              {dimLayout ? <SheetSvg nested layout={dimLayout} tint={tint} /> : null}
              {levelLayout ? <SheetSvg nested layout={levelLayout} tint={tint} /> : null}
              {op?.kind === "window" ? <rect x={Math.min(op.a.x, op.b.x)} y={-Math.max(op.a.y, op.b.y)} width={Math.abs(op.b.x - op.a.x)} height={Math.abs(op.b.y - op.a.y)} fill={op.b.x < op.a.x ? "rgba(46,160,67,0.12)" : "rgba(47,111,219,0.12)"} stroke={op.b.x < op.a.x ? "#2EA043" : tint} strokeDasharray={op.b.x < op.a.x ? "5 3" : undefined} vectorEffect="non-scaling-stroke" /> : null}
              {(op?.kind === "second" || op?.kind === "value") ? <line x1={op.base.x} y1={-op.base.y} x2={op.cur.x} y2={-op.cur.y} stroke={tint} strokeDasharray="4 3" vectorEffect="non-scaling-stroke" /> : null}
              {hover?.snap ? <rect x={hover.snap.x - pxToM(5)} y={-hover.snap.y - pxToM(5)} width={pxToM(10)} height={pxToM(10)} fill="none" stroke="#E6A817" strokeWidth={1.5} vectorEffect="non-scaling-stroke" /> : null}
            </svg>
          ) : null}
          {op?.kind === "tableinput" ? (
            <div className="absolute z-20 grid gap-1 bg-white border border-cad-strong shadow p-2 text-[11px] w-[420px]" style={toScreen(op.p)} role="dialog" aria-label={t("Tablo ekle")}>
              <span className="font-semibold">{t("Tablo ekle")}</span>
              <span className="text-cad-ink3">{t("Her satır bir tablo satırı; hücreleri sekme (Excel'den kopyala-yapıştır) ya da ; ile ayırın. İlk satır başlık olur.")}</span>
              <textarea autoFocus rows={6} className="cad-input !h-auto font-mono p-1" aria-label={t("Tablo içeriği")} value={op.value} placeholder={"Poz;Açıklama;Miktar\n1;Kazı;12,5 m³"}
                onChange={(e) => setOp({ ...op, value: e.target.value })} onKeyDown={(e) => { if (e.key === "Escape") { e.preventDefault(); setOp(null); } else if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); finishTable(op); } e.stopPropagation(); }} />
              <div className="flex justify-end gap-1"><button type="button" className="cad-btn-box" onClick={() => setOp(null)}>{t("Vazgeç")}</button><button type="button" className="cad-btn-primary" onClick={() => finishTable(op)}>{t("Ekle")} (Ctrl+Enter)</button></div>
            </div>
          ) : null}
          {op?.kind === "textinput" || op?.kind === "value" ? (
            <div className="absolute z-20 flex items-center gap-1 bg-white border border-cad-strong shadow px-1.5 py-1 text-[11px]" style={toScreen(op.kind === "textinput" ? op.p : op.base)}>
              <span className="text-cad-ink3">{op.kind === "textinput" ? t("Yazı") : ed.tool === "scale" ? t("Ölçek çarpanı") : t("Açı (°)")}</span>
              {op.kind === "textinput" && op.multi ? (
                <textarea autoFocus className="cad-input w-[380px] !h-auto py-1 font-mono leading-snug resize" rows={Math.min(14, op.value.split("\n").length + 1)} aria-label={t("Yazı")} title={t("Enter yeni satır · Ctrl+Enter bitir · Esc vazgeç")} value={op.value}
                  onChange={(e) => setOp({ ...op, value: e.target.value })}
                  onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); finishText(op); } else if (e.key === "Escape") { e.preventDefault(); setOp(null); } e.stopPropagation(); }} />
              ) : (
              <input autoFocus className="cad-input w-[220px]" aria-label={op.kind === "textinput" ? t("Yazı") : t("Değer")} value={op.kind === "textinput" ? op.value : op.input}
                onChange={(e) => setOp(op.kind === "textinput" ? { ...op, value: e.target.value } : { ...op, input: e.target.value })}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (op.kind === "textinput") finishText(op); else commitValue(op); } else if (e.key === "Escape") { e.preventDefault(); setOp(null); } e.stopPropagation(); }} />
              )}
              {op.kind === "textinput" && op.h ? <><span className="text-cad-ink3">h</span><input className="cad-input w-[60px] font-mono" aria-label={t("Yazı yüksekliği (m)")} value={String(Math.round(op.h * 1000) / 1000).replace(".", ",")} onChange={(e) => { const v = parseTR(e.target.value); if (v) setOp({ ...op, h: v }); }} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); finishText(op); } e.stopPropagation(); }} /></> : null}
              <button type="button" className="cad-btn-primary !h-[22px]" onClick={() => (op.kind === "textinput" ? finishText(op) : commitValue(op))}>OK</button>
            </div>
          ) : null}
          <div className="absolute left-2 top-2 flex gap-1 text-[11px]">
            <span className="px-2 py-0.5 bg-cad-tip border border-cad-tipline text-cad-tipink rounded-[2px] font-mono">{layout ? `${sheet.paper === "auto" ? t("Otomatik") : sheet.paper} · ${Math.round(layout.paperMm.w)} × ${Math.round(layout.paperMm.h)} mm · 1/${layout.scale}` : "—"}</span>
            {layout?.staleEdits ? <span role="status" className="px-2 py-0.5 bg-cad-warnbg text-cad-warnfg border border-cad-tipline rounded-[2px]">⚠️ {layout.staleEdits} {t("düzenleme duvar değiştiği için uygulanmadı")}</span> : null}
          </div>
          <div className="absolute right-2 top-2 flex gap-1">
            <button type="button" className="cad-btn-box" onClick={() => setVb(null)}>{t("Sığdır")}</button>
            {editCount && !readOnly ? <button type="button" className="cad-btn-box" onClick={() => ed.request("reset")}>{t("Düzenlemeleri sıfırla")}</button> : null}
          </div>
          {full && layout ? <div role="status" aria-label={t("Komut istemi")} className="absolute left-2 bottom-2 px-2 py-1 bg-cad-tip border border-cad-tipline text-cad-tipink rounded-[2px] font-mono text-[11px]">{prompt}{hover ? `   ${fmt(hover.p.x, 2)}, ${fmt(hover.p.y, 2)}` : ""}</div> : null}
        </div>
        <LayoutTabs current="drawing" />
        <CommandLine disabled={readOnly} />
      </div>
    </>
  );
}

/** Pafta çizimi yalnız düzen değişince yeniden çizilir (fare hareketinde değil) */
const MemoSheet = memo(function MemoSheet({ layout }: { layout: SheetLayout }) { return <SheetSvg nested layout={layout} />; });
/**
 * İki nokta + yer noktasından doğrusal ölçü (AutoCAD DIMLINEAR): yer noktası iki noktanın x aralığı içindeyse ya da dikeyde daha
 * uzaktaysa yatay ölçü, değilse düşey. Öteleme birinci noktadan ölçülür.
 */
function dimFrom(a: Pt, b: Pt, at: Pt): { k: "dim"; a: Pt; b: Pt; offset: number; dir: "h" | "v" } {
  const inX = at.x >= Math.min(a.x, b.x) && at.x <= Math.max(a.x, b.x), inY = at.y >= Math.min(a.y, b.y) && at.y <= Math.max(a.y, b.y);
  const horizontal = inX || (!inY && Math.abs(at.y - (a.y + b.y) / 2) >= Math.abs(at.x - (a.x + b.x) / 2));
  return horizontal ? { k: "dim", a, b, offset: at.y - a.y, dir: "h" } : { k: "dim", a, b, offset: at.x - a.x, dir: "v" };
}

/**
 * Pafta noktasının kotu: noktayı içeren kesit/görünüş bloğunun kaydırması çıkarılıp model y'sine dönülür (ikisi de aynı düşey başlangıçta);
 * kot = mutlak kot + (y − referans). Blok dışındaysa kesitin kaydırması kullanılır.
 */
export function levelAt(layout: SheetLayout, p: Pt): number {
  const inside = layout.blocks.filter((b) => (b.id === "kesit" || b.id === "gorunus") && !b.hidden).find((b) => p.x >= b.box.minX + b.offset.x && p.x <= b.box.maxX + b.offset.x && p.y >= b.box.minY + b.offset.y && p.y <= b.box.maxY + b.offset.y);
  const blk = inside ?? layout.blocks.find((b) => b.id === "kesit");
  const y = p.y - (blk?.offset.y ?? 0);
  return Math.round((layout.level.datum + (y - layout.level.zero)) * 100) / 100;
}
const fmtLevel = (v: number): string => `${v > 1e-9 ? "+" : v < -1e-9 ? "−" : "±"}${fmt(Math.abs(v), 2)}`;

/** Tablo metninden ızgara + hücre yazıları (sütun genişliği en uzun hücreye göre) */
export function tablePrims(p: Pt, text: string, th: number): Record<string, unknown>[] {
  const rows = text.split(/\r?\n/).map((l) => l.trimEnd()).filter((l) => l.trim()).map((l) => l.split(l.includes("\t") ? "\t" : ";").map((c) => c.trim()));
  if (!rows.length) return [];
  const n = Math.max(...rows.map((r) => r.length));
  const h = th * 0.85, rowH = th * 2.2, pad = th * 0.5;
  const widths = Array.from({ length: n }, (_, j) => Math.max(th * 3, ...rows.map((r) => (r[j] ?? "").length * h * 0.6)) + 2 * pad);
  const W = widths.reduce((a, b) => a + b, 0), H = rows.length * rowH;
  const out: Record<string, unknown>[] = [];
  for (let i = 0; i <= rows.length; i++) out.push({ k: "line", layer: "KULLANICI", a: { x: p.x, y: p.y - i * rowH }, b: { x: p.x + W, y: p.y - i * rowH }, ...(i <= 1 || i === rows.length ? { extra: { lineweight: 35 } } : {}) });
  let x = p.x;
  for (let j = 0; j <= n; j++) { out.push({ k: "line", layer: "KULLANICI", a: { x, y: p.y }, b: { x, y: p.y - H } }); x += widths[j] ?? 0; }
  rows.forEach((r, i) => { let cx = p.x; r.forEach((c, j) => { if (c) out.push({ k: "text", layer: i === 0 ? "YAZI" : "KULLANICI", p: { x: cx + pad, y: p.y - i * rowH - rowH + th * 0.7 }, value: c, opts: { h } }); cx += widths[j]!; }); });
  return out;
}

/** Eklenen öğe olarak saklanacak biçim: blok/anahtar bilgisi atılır */
function strip(e: SheetPrim): Record<string, unknown> { const { key: _k, fp: _f, g: _g, ...rest } = e; return rest as Record<string, unknown>; }

/** Seçili duvarın paftası (şerit ve komutlar için): DXF indir */
export function downloadSheetDxf(wall: WallInput, result: WallResult, projectName: string, fileBase: string, digest: string): void {
  downloadBytes(`${fileBase}-${wall.name.toLocaleLowerCase("tr").replace(/[^a-z0-9]+/g, "-")}.dxf`, encodeDxf(buildDxf(wall, result, sheetOptions(projectName, digest))), "application/dxf");
}

/** Yazdır / PDF: paftayı kâğıt boyutunda (@page) gerçek ölçekte, siyah çizgilerle gizli bir çerçevede basar. */
export function printSheet(wall: WallInput, result: WallResult, projectName: string, digest: string): void {
  if (!requireFullAccess(t("Çizim paftası yazdırma"))) return;
  const layout = buildSheet(wall, result, sheetOptions(projectName, digest));
  const f = layout.frame;
  const svg = renderToStaticMarkup(<SheetSvg layout={layout} mode="print" viewBox={`${f.minX} ${-f.maxY} ${f.maxX - f.minX} ${f.maxY - f.minY}`} />);
  const iframe = document.createElement("iframe");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0";
  document.body.appendChild(iframe);
  const doc = iframe.contentDocument!;
  doc.open();
  doc.write(`<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(projectName)} — ${escapeHtml(wall.name)}</title><style>@page{size:${layout.paperMm.w.toFixed(1)}mm ${layout.paperMm.h.toFixed(1)}mm;margin:0}html,body{margin:0;padding:0}svg{display:block}</style></head><body>${svg}</body></html>`);
  doc.close();
  const go = (): void => { iframe.contentWindow?.focus(); iframe.contentWindow?.print(); setTimeout(() => iframe.remove(), 60_000); };
  if (doc.readyState === "complete") setTimeout(go, 50); else iframe.onload = go;
}

const escapeHtml = (s: string): string => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!);
